import * as React from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Copy, Loader2, Search, Shield, ShieldCheck, SquarePen, Users } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { usePortal } from "@/features/portal/portal-context";
import { brandPath } from "@/features/portal/navigation";
import { fetchAllBrands, toAdminBrandRow, type AdminBrandRow, type AdminMember } from "@/data/brands-api";
import { Button } from "@/components/ui/button";
import { MembersDialog } from "@/features/admin/members-dialog";

type StatusFilter = "ALL" | "ACTIVE" | "DRAFT";

/** Brands the directory pulls in one page. Most deployments sit well under this;
 *  a banner warns if the database holds more than we loaded. */
const PAGE_SIZE = 200;

/** Render an epoch timestamp (seconds or ms) as a compact relative string. */
function formatRelative(ts: number): string {
  if (!ts) return "—";
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const diff = Date.now() - ms;
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (diff < min) return "just now";
  if (diff < hr) return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hr)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Gradient (or photo) avatar for a brand. */
function BrandAvatar({ initials, size = 36 }: { initials: string; size?: number }) {
  return (
    <div
      className="grid shrink-0 place-items-center rounded-[9px] bg-brand-gradient font-extrabold text-white"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initials}
    </div>
  );
}

/** Up-to-three overlapping member avatars, with a "+N" overflow chip. */
function MemberStack({ members }: { members: AdminMember[] }) {
  if (members.length === 0) return <span className="text-[12.5px] text-text-3">None</span>;
  const shown = members.slice(0, 3);
  const extra = members.length - shown.length;
  return (
    <div className="flex items-center">
      {shown.map((m, i) => (
        <div
          key={m.id}
          title={`${m.name}${m.role === "OWNER" ? " · Owner" : ""}`}
          className={cn(
            "grid size-6 place-items-center overflow-hidden rounded-full border-2 border-card text-[9.5px] font-bold text-white",
            !m.photoURL && "bg-brand-gradient",
          )}
          style={{ marginLeft: i === 0 ? 0 : -7 }}
        >
          {m.photoURL ? (
            <img src={m.photoURL} alt={m.name} className="size-full object-cover" />
          ) : (
            m.name.slice(0, 2).toUpperCase()
          )}
        </div>
      ))}
      {extra > 0 && (
        <div
          className="grid size-6 place-items-center rounded-full border-2 border-card bg-panel-2 text-[9.5px] font-bold text-muted-foreground"
          style={{ marginLeft: -7 }}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: "ACTIVE" | "DRAFT" }) {
  const active = status === "ACTIVE";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-bold",
        active ? "bg-[rgba(22,163,74,.12)] text-[#16a34a] dark:text-[#4ade80]" : "bg-[rgba(245,185,69,.16)] text-[#b9851f] dark:text-[#f5b945]",
      )}
    >
      <span className="size-1.5 rounded-full bg-current" />
      {active ? "Active" : "Draft"}
    </span>
  );
}

/** A labeled field in a brand card's meta grid. Uppercase caption over its value. */
function MetaField({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <div className="text-[10.5px] font-bold uppercase tracking-[.05em] text-text-3">{label}</div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function AllBrandsPage() {
  const { cacheBrand } = usePortal();
  const navigate = useNavigate();

  const [rows, setRows] = React.useState<AdminBrandRow[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");
  const [status, setStatus] = React.useState<StatusFilter>("ALL");
  const [managing, setManaging] = React.useState<AdminBrandRow | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { nodes, totalCount } = await fetchAllBrands({ first: PAGE_SIZE });
      setRows(nodes.map(toAdminBrandRow));
      setTotalCount(totalCount);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : "Couldn’t load brands.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  function copyId(id: string) {
    navigator.clipboard?.writeText(id).then(
      () => toast.success("Brand ID copied"),
      () => toast.error("Couldn’t copy to clipboard"),
    );
  }

  function open(row: AdminBrandRow) {
    // Seed the brand from the row we already have, then deep-link to its editor —
    // a shareable URL, and BrandRoute won't need to refetch it.
    cacheBrand(row.raw);
    toast.success(`Opened “${row.name}” in the editor`);
    navigate(brandPath(row.id));
  }

  /** Reflect a membership change made in the dialog back into the directory row. */
  function syncMembers(brandId: string, members: AdminMember[]) {
    setRows((rs) => rs.map((r) => (r.id === brandId ? { ...r, members } : r)));
    setManaging((m) => (m && m.id === brandId ? { ...m, members } : m));
  }

  const counts = React.useMemo(
    () => ({
      active: rows.filter((r) => r.status === "ACTIVE").length,
      draft: rows.filter((r) => r.status === "DRAFT").length,
      mine: rows.filter((r) => r.viewerRole !== null).length,
    }),
    [rows],
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== "ALL" && r.status !== status) return false;
      if (!q) return true;
      return r.name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q);
    });
  }, [rows, query, status]);

  const SEGMENTS: { key: StatusFilter; label: string; count: number }[] = [
    { key: "ALL", label: "All", count: rows.length },
    { key: "ACTIVE", label: "Active", count: counts.active },
    { key: "DRAFT", label: "Draft", count: counts.draft },
  ];

  return (
    <div>
      <h1 className="mb-1.5 text-2xl font-extrabold tracking-[-.02em]">All brands</h1>
      <p className="mb-[22px] max-w-[620px] text-sm text-muted-foreground">
        Every brand on pop.in. As a super admin you can open and edit any brand — including ones you
        don’t own.
      </p>

      {/* Admin-access notice */}
      <div className="mb-[22px] flex items-start gap-[11px] rounded-2xl border border-border bg-panel-2 p-[13px_15px]">
        <div className="grid size-[30px] shrink-0 place-items-center rounded-[9px] bg-[rgba(245,185,69,.16)] text-[#b9851f] dark:text-[#f5b945]">
          <ShieldCheck className="size-[17px]" />
        </div>
        <p className="text-[12.5px] leading-relaxed text-muted-foreground">
          <b className="font-bold text-foreground">Admin access.</b> Opening a brand loads it into the
          editor — the same Theming, Terminology and Demo screens owners use. Changes are saved
          against that brand and attributed to you.
        </p>
      </div>

      {/* Stats */}
      <div className="mb-[18px] flex flex-wrap gap-3">
        {[
          { n: totalCount, l: "Total brands", cls: "" },
          { n: counts.active, l: "Active", cls: "text-[#16a34a] dark:text-[#4ade80]" },
          { n: counts.draft, l: "Drafts", cls: "text-[#b9851f] dark:text-[#f5b945]" },
          { n: counts.mine, l: "You’re a member of", cls: "" },
        ].map((s) => (
          <div
            key={s.l}
            className="min-w-[150px] flex-1 rounded-2xl border border-border bg-card p-[14px_16px] shadow-portal"
          >
            <div className={cn("text-[22px] font-extrabold tracking-[-.02em]", s.cls)}>{s.n}</div>
            <div className="mt-0.5 text-[11.5px] text-text-3">{s.l}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
        <label className="flex h-[38px] min-w-[240px] flex-1 items-center gap-2 rounded-[10px] border border-border bg-field px-3">
          <Search className="size-4 text-text-3" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by brand name or ID…"
            className="flex-1 bg-transparent text-[13.5px] text-foreground outline-none placeholder:text-text-3"
          />
        </label>
        <div className="flex rounded-[10px] border border-border bg-field p-[3px]">
          {SEGMENTS.map((seg) => (
            <button
              key={seg.key}
              type="button"
              onClick={() => setStatus(seg.key)}
              className={cn(
                "rounded-lg px-3 py-1.5 text-[12.5px] font-semibold transition-all",
                status === seg.key
                  ? "bg-card text-foreground shadow-portal"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {seg.label}
              <span className="ml-1.5 font-bold text-text-3">{seg.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Brand list — cards reflow gracefully instead of a wide table that clips
          its Actions column on narrow/mobile widths. */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card py-16 text-sm text-muted-foreground shadow-portal">
          <Loader2 className="size-4 animate-spin" /> Loading brands…
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card py-16 text-center shadow-portal">
          <AlertCircle className="size-6 text-[#ff7a7a]" />
          <div className="text-sm text-muted-foreground">{error}</div>
          <Button variant="subtle" size="sm" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card py-16 text-center shadow-portal">
          <Shield className="size-6 text-text-3" />
          <div className="text-sm text-muted-foreground">
            {rows.length === 0 ? "No brands yet." : "No brands match your search."}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((row) => (
            <div
              key={row.id}
              className="rounded-2xl border border-border bg-card p-4 shadow-portal transition-colors hover:border-border-strong"
            >
              {/* Identity + actions */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <BrandAvatar initials={row.initials} />
                  <div className="flex min-w-0 items-center gap-2 text-[14px] font-bold text-foreground">
                    <span className="truncate">{row.name}</span>
                    {row.viewerRole && (
                      <span className="shrink-0 rounded-[5px] border border-border-strong px-1.5 py-px text-[9.5px] font-extrabold tracking-[.04em] text-brand">
                        YOU · {row.viewerRole}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button variant="subtle" size="sm" onClick={() => setManaging(row)}>
                    <Users className="size-4" /> Members
                  </Button>
                  <Button size="sm" onClick={() => open(row)}>
                    <SquarePen className="size-4" /> Open
                  </Button>
                </div>
              </div>

              {/* Meta — aligned grid. Brand ID gets its own full-width row so the
                  opaque id is never truncated; the rest sit 4-across on desktop,
                  2-across on mobile. */}
              <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-4 border-t border-border pt-4 md:grid-cols-4">
                <MetaField label="Brand ID" className="col-span-2 md:col-span-4">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <code className="font-mono text-[11.5px] text-text-3">{row.id}</code>
                    <button
                      type="button"
                      title="Copy brand ID"
                      onClick={() => copyId(row.id)}
                      className="grid shrink-0 place-items-center rounded-md border border-border bg-panel-2 p-1 text-text-3 transition-colors hover:text-brand"
                    >
                      <Copy className="size-3" />
                    </button>
                  </div>
                </MetaField>
                <MetaField label="Status">
                  <StatusPill status={row.status} />
                </MetaField>
                <MetaField label="Themes">
                  <span className="text-[12.5px] text-muted-foreground">{row.themeCount} themes</span>
                </MetaField>
                <MetaField label="Members">
                  <MemberStack members={row.members} />
                </MetaField>
                <MetaField label="Updated">
                  <span className="whitespace-nowrap text-[12.5px] text-text-3">
                    {formatRelative(row.updatedAt)}
                  </span>
                </MetaField>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footnote */}
      {!loading && !error && (
        <div className="mt-3.5 flex items-center justify-between text-xs text-text-3">
          <div>
            Showing <b className="text-muted-foreground">{filtered.length}</b> of {rows.length} loaded
            {totalCount > rows.length && (
              <span className="ml-1 text-[#b9851f] dark:text-[#f5b945]">
                · {totalCount} total in database (showing first {PAGE_SIZE})
              </span>
            )}
          </div>
        </div>
      )}

      <MembersDialog
        row={managing}
        onOpenChange={(open) => !open && setManaging(null)}
        onMembersChange={syncMembers}
      />
    </div>
  );
}
